/* eslint-disable @typescript-eslint/naming-convention */

import wcShared from '@web-components/shared';

import { store } from './store';

/**
 * Indicate that the file is a module
 * Ref https://stackoverflow.com/a/59499895
 */
export {};

type Challenge = 'incoming_call' | 'sms' | 'mail' | 'app' | 'qr-code' | 'captcha';
declare global {
  interface Window {
    /**
     * Triggers verification using the shield module
     *
     * The shield module allows verification of a user's identity using different challenge methods.
     * - It can be used as a CustomElement or as a global function.
     * - If the API returns a HTTP 412 response with a uuid, it means that this request requires verification see RFC
     *
     * REFS:
     * - Documentation https://gitlab.infomaniak.ch/infomaniak/front/web-components/-/merge_requests/1301
     * - RFC https://gitlab.infomaniak.ch/infomaniak/site-api-shield/-/blob/dd73186a8fc6c71dac965ed6f1378fe7993fa09d/README.md#rfc
     * - Figma https://www.figma.com/design/db9Uy5VwRdxS79xY0R7UGU/kSuite---Transformation-des-clients-vers-kSuite?node-id=3062-63666
     */
    ikShieldVerify: ((
      payload:
        | string
        | {
            action_slug: string;
            password?: string;
            phone?: string;
            locale?: string;
          },
      arg?: {
        /** *(optional)* The DOM Node to mount the ikShield component in, defaults to document.body */
        target?: HTMLElement;
        attrs?: {
          /** *(optional)* The container height */
          height?: number;
          /** *(optional)* The loading state */
          loading?: boolean;
          /** *(optional)* Should the challenge be displayed inside a modal */
          modal?: boolean;
          /** *(optional)* The container width */
          width?: boolean;
          /** *(optional)* The current challenge to use */
          challenge?: Challenge | null;
          /** *(optional)* List of available challenges, comma separated values */
          challenges?: string;
          /** *(optional)* Allow closing the modal */
          closable?: boolean;
          /** *(optional)* The timeout to close the success message window */
          'close-timeout'?: number;
          /** (optional) Show errors in a notification */
          'error-notifications'?: boolean;
          /** (optional) Errors notification duration */
          'error-notification-duration'?: number;
          /** (optional) Can the error notification be dismissed */
          'error-notification-dismissible'?: boolean;
          /** *(optional)* The polling rate to check incoming call challenge (ms) */
          'incoming-call-polling-rate'?: number;
          /** *(optional)* Incoming call challenge phone number options by phone codes */
          'incoming-call-phone-number-by-country-code'?: { [k: string]: string };
          /** *(optional)* The displayed user's phone number */
          'phone-number'?: string;
          /** *(optional)*  Prevent closing the modal on backdrop click */
          'prevent-close-on-backdrop-click'?: boolean;
          /** *(optional)*  Prevent closing the modal on `Escape` */
          'prevent-close-on-escape'?: boolean;
          /** *(optional)* The rate-limiting date at which to retry the challenge */
          'retry-at'?: string;
          /** *(optional)* The ISO date at which the sms otp expires at */
          'sms-expires-at'?: string;
          /** *(optional)* Pattern for the expected sms code */
          'sms-pattern'?: string;
        };
      },
    ) => Promise<{
      uuid: null | string;
      challenge: null | Challenge;
    }>) & {
      /** Display a failure message */
      failed: (notificationOptions: {
        /** The message to be displayed in the notification */
        message: string;
        /** *(optional)* The duration to display the message */
        duration?: number;
        /** *(optional)* Can the notification be dismissed */
        dismissible?: boolean;
      }) => void;

      /** Reset the state */
      reset: () => void;
    };
  }
}

/** Setup the global .ikShieldVerify() method */
window.ikShieldVerify = Object.assign(
  async (...args: Parameters<Window['ikShieldVerify']>) => {
    const [payload, arg] = args;
    const target = arg?.target ?? document.body;
    const attrs = target === document.body ? { modal: true, ...arg?.attrs } : {};

    // Mount the element into the document.body
    const elm = document.createElement('wc-module-shield');
    for (const [key, value] of Object.entries(attrs)) {
      elm.setAttribute(key, value?.toString() ?? '');
    }
    target.appendChild(elm);

    // Trigger the .verify() method on the element
    const promise = elm.verify(payload);

    // Upon promise resolution, remove the created element
    (async () => {
      await promise;
      target.removeChild(elm);
    })();

    return promise;
  },
  {
    failed: (...args: Parameters<Window['ikShieldVerify']['failed']>) =>
      wcShared?.notificationService?.showError(...args),
    reset: () => {
      store.reset();
    },
  },
);
